import React, { useEffect, useState, useMemo, useCallback } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import {
  FileSpreadsheet, CheckCircle2, Mail, Trash2, Search, RefreshCw, Loader2,
  RotateCcw, Save, CreditCard,
} from "lucide-react";
import { useApp } from "../../contexts/AppContext";

const PROVIDER_LABEL = {
  stripe: "Cartão (Stripe)", paypal: "PayPal", mbway: "MB WAY",
  multibanco: "Multibanco", pix: "PIX", manual: "Manual",
};
const CUR_SYM = { eur: "€", brl: "R$", gbp: "£", usd: "$" };
const money = (amount, currency) => {
  const c = (currency || "eur").toLowerCase();
  const amt = (amount || 0).toFixed(2);
  return c === "eur" ? `${amt} €` : `${CUR_SYM[c] || c.toUpperCase()}${amt}`;
};

export default function AdminEnrollments() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all"); // all | paid | pending
  const [studentsMin, setStudentsMin] = useState("");
  const [studentsMax, setStudentsMax] = useState("");
  const [studentsSort, setStudentsSort] = useState(null); // null | "asc" | "desc"

  // Pagamento & Resultados config (moved from standalone Payments tab)
  const { settingsRaw: settings, refreshSettings } = useApp();
  const [cfg, setCfg] = useState(null);
  const [savingCfg, setSavingCfg] = useState(false);
  const [showCfg, setShowCfg] = useState(false);
  useEffect(() => {
    if (!settings || cfg) return;
    setCfg({
      enrollment_price_pt: settings.enrollment_price_pt ?? settings.enrollment_price_eur ?? 0.50,
      enrollment_price_br: settings.enrollment_price_br ?? 2.50,
      enrollment_price_en: settings.enrollment_price_en ?? settings.enrollment_price_gbp ?? 0.40,
      enrollment_currency_pt: settings.enrollment_currency_pt ?? "eur",
      enrollment_currency_br: settings.enrollment_currency_br ?? "brl",
      enrollment_currency_en: settings.enrollment_currency_en ?? "gbp",
      payment_instructions_pt: settings.payment_instructions_pt || "",
      payment_instructions_br: settings.payment_instructions_br || "",
      payment_instructions_en: settings.payment_instructions_en || "",
      results_show_count: settings.results_show_count ?? 10,
    });
  }, [settings, cfg]);
  const setC = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  const saveCfg = async () => {
    setSavingCfg(true);
    try {
      await api.put("/admin/settings_x", cfg);
      await refreshSettings();
      toast.success("Configuração guardada");
    } catch (_) { toast.error("Erro ao guardar"); }
    finally { setSavingCfg(false); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/enrollments");
      setItems(data || []);
      setSelected(new Set());
    } catch (_) { toast.error("Erro a carregar inscrições"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
    const sMin = studentsMin.trim() === "" ? null : Number(studentsMin);
    const sMax = studentsMax.trim() === "" ? null : Number(studentsMax);
    const list = items.filter((e) => {
      if (filter === "paid" && !e.payment_confirmed) return false;
      if (filter === "pending" && e.payment_confirmed) return false;
      const n = Number(e.num_students || 0);
      if (sMin !== null && !Number.isNaN(sMin) && n < sMin) return false;
      if (sMax !== null && !Number.isNaN(sMax) && n > sMax) return false;
      if (terms.length === 0) return true;
      const lp = e.latest_payment || {};
      const hay = [
        e.code, e.teacher_name, e.teacher_email, e.school, e.class_name,
        e.quiz_name_pt, e.num_students, (e.created_at || "").slice(0, 10),
        e.payment_confirmed ? "pago confirmado" : "pendente",
        e.credentials_sent ? "credenciais enviadas" : "",
        PROVIDER_LABEL[lp.provider] || lp.provider || "",
        lp.doc_ref || e.doc_ref || "", lp.reference || "", lp.entity || "",
      ].map((v) => String(v ?? "").toLowerCase()).join(" ");
      return terms.every((t) => hay.includes(t));
    });
    if (studentsSort) {
      list.sort((a, b) => {
        const diff = Number(a.num_students || 0) - Number(b.num_students || 0);
        return studentsSort === "asc" ? diff : -diff;
      });
    }
    return list;
  }, [items, query, filter, studentsMin, studentsMax, studentsSort]);

  const cycleStudentsSort = () =>
    setStudentsSort((s) => (s === null ? "asc" : s === "asc" ? "desc" : null));

  const toggleOne = (id) => setSelected((prev) => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });
  const allSelected = filtered.length > 0 && filtered.every((e) => selected.has(e.id));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(filtered.map((e) => e.id)));

  const confirmPayment = async (enr) => {
    if (!window.confirm("Marcar o pagamento como PAGO? As credenciais NÃO serão enviadas (clica em \"Enviar credenciais\" depois).")) return;
    try {
      if (enr.latest_payment?.id && enr.latest_payment.payment_status !== "paid") {
        await api.post(`/admin/payments/${enr.latest_payment.id}/mark-paid`);
      } else {
        await api.post(`/admin/enrollments/${enr.id}/confirm-payment`);
      }
      toast.success("Pagamento marcado como pago");
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
    load();
  };

  const revertPayment = async (enr) => {
    if (!enr.latest_payment?.id) {
      toast.error("Sem transação associada para reverter");
      return;
    }
    if (!window.confirm("Reverter este pagamento para PENDENTE?")) return;
    try {
      await api.post(`/admin/payments/${enr.latest_payment.id}/mark-pending`);
      toast.success("Revertido para pendente");
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
    load();
  };

  const sendCreds = async (e) => {
    const lang = window.confirm("OK = enviar versão PT, Cancelar = enviar versão EN") ? "pt" : "en";
    toast.message("A enviar...");
    try {
      const { data } = await api.post(`/admin/enrollments/${e.id}/send-credentials`, null, { params: { lang } });
      if (data.ok) toast.success(`Credenciais enviadas (${data.count}) para ${e.teacher_email}`);
      else toast.error("Falha no envio");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };

  const removeEnrollment = async (id) => {
    if (!window.confirm("Eliminar esta inscrição, as credenciais e pagamentos associados?")) return;
    await api.delete(`/admin/enrollments/${id}`);
    load();
  };
  const removeAll = async () => {
    if (items.length === 0) return;
    if (!window.confirm(`Eliminar TODAS as ${items.length} inscrições, credenciais e resultados associados? Esta ação é irreversível.`)) return;
    if (!window.confirm("Tem mesmo a certeza?")) return;
    try {
      const { data } = await api.delete("/admin/enrollments");
      toast.success(`${data.deleted} inscrição(ões) eliminada(s)`);
      load();
    } catch (_) { toast.error("Erro"); }
  };
  const removeSelected = async () => {
    const ids = [...selected];
    if (ids.length === 0) return;
    if (!window.confirm(`Eliminar ${ids.length} inscrição(ões) seleccionada(s)?`)) return;
    try {
      const { data } = await api.post("/admin/enrollments/delete-bulk", { ids });
      toast.success(`${data.deleted} inscrição(ões) eliminada(s)`);
      load();
    } catch (_) { toast.error("Erro"); }
  };

  const exportExcel = async () => {
    try {
      const res = await api.get("/admin/enrollments/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "inscricoes.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch (_) { toast.error("Erro ao exportar"); }
  };

  const sync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/admin/payments/sync");
      toast.success(`Sincronização concluída: ${data.confirmed} pagamento(s) confirmado(s) (Stripe: ${data.stripe ?? 0}, Multibanco/PIX: ${data.ifthenpay ?? 0}).`);
      load();
    } catch (_) { toast.error("Erro ao sincronizar"); }
    finally { setSyncing(false); }
  };

  const updateDocRef = (txId, doc_ref) => setItems((prev) => prev.map((e) =>
    e.latest_payment?.id === txId ? { ...e, latest_payment: { ...e.latest_payment, doc_ref } } : e
  ));
  const updateEnrollmentDocRef = (enrId, doc_ref) => setItems((prev) => prev.map((e) =>
    e.id === enrId ? { ...e, doc_ref } : e
  ));

  // Totals by currency (paid vs unpaid)
  const fallbackAmount = (e) => {
    const lang = (e.quiz_language || "pt").toLowerCase();
    const key = ["pt", "br", "en"].includes(lang) ? lang : "pt";
    const price = Number(cfg[`enrollment_price_${key}`] || 0);
    const cur = cfg[`enrollment_currency_${key}`] || "eur";
    return { amount: price * Number(e.num_students || 0), currency: cur };
  };
  const paidByCurrency = {};
  const unpaidByCurrency = {};
  items.forEach((e) => {
    const lp = e.latest_payment;
    const isPaid = !!e.payment_confirmed;
    let amount, currency;
    if (lp && lp.amount != null) {
      amount = lp.amount || 0;
      currency = (lp.currency || "eur").toLowerCase();
    } else {
      const f = fallbackAmount(e);
      amount = f.amount;
      currency = (f.currency || "eur").toLowerCase();
    }
    const bucket = isPaid ? paidByCurrency : unpaidByCurrency;
    bucket[currency] = (bucket[currency] || 0) + amount;
  });
  const fmtByCurrency = (map) => {
    if (Object.keys(map).length === 0) return "0.00 €";
    return Object.entries(map)
      .map(([c, v]) => (c === "eur" ? `${v.toFixed(2)} €` : `${CUR_SYM[c] || c.toUpperCase()}${v.toFixed(2)}`))
      .join(" · ");
  };
  const totalPaidLabel = fmtByCurrency(paidByCurrency);
  const totalUnpaidLabel = fmtByCurrency(unpaidByCurrency);

  // Paid / unpaid counts
  const paidCount = items.reduce((n, e) => n + (e.payment_confirmed ? 1 : 0), 0);
  const unpaidCount = items.length - paidCount;

  const fmtDate = (iso) => { try { return new Date(iso).toLocaleString("pt-PT"); } catch { return iso; } };
  const fmtDateShort = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      const pad = (n) => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    } catch { return iso; }
  };

  return (
    <div data-testid="admin-enrollments-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <h1 className="font-outfit text-2xl font-bold text-[#0B1D3A] flex items-center gap-2">
            <CreditCard size={22} /> Inscrições & Pagamentos
          </h1>
          <p className="text-sm text-[#0B1D3A]/60" data-testid="enroll-totals">
            <strong>{items.length}</strong> total · <strong className="text-[#2D9C5A]">{paidCount}</strong> pagas (<strong className="text-[#2D9C5A]">{totalPaidLabel}</strong>) · <strong className="text-[#D4572A]">{unpaidCount}</strong> não pagas (<strong className="text-[#D4572A]">{totalUnpaidLabel}</strong>)
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={sync} disabled={syncing} data-testid="payments-sync-btn"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-[#0B1D3A] text-white font-semibold text-sm disabled:opacity-60">
            {syncing ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />} Sincronizar pagamentos
          </button>
          <button onClick={exportExcel} className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-[#2D9C5A] text-white font-semibold text-sm" data-testid="enroll-export-btn"><FileSpreadsheet size={15} /> Exportar Excel</button>
          <button onClick={() => setShowCfg((v) => !v)} className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white border border-[#0B1D3A]/15 text-[#0B1D3A] font-semibold text-sm" data-testid="toggle-pay-config">
            {showCfg ? "Ocultar configuração" : "Configuração de pagamentos"}
          </button>
          <a href="/admin/payment-credentials" className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white border border-[#0B1D3A]/15 text-[#0B1D3A] font-semibold text-sm hover:bg-[#0B1D3A]/5" data-testid="pc-link">
            Credenciais Stripe / PayPal / ifthenpay
          </a>
        </div>
      </div>

      {showCfg && cfg && (
        <div className="rounded-2xl bg-white p-5 border border-[#0B1D3A]/5 space-y-4 mb-6" data-testid="payments-config-section">
          <h2 className="font-outfit font-bold text-[#0B1D3A]">Pagamento & Resultados</h2>
          <p className="text-xs text-[#0B1D3A]/60">Define o <b>valor por aluno</b> e a <b>moeda</b> para cada versão. Total = valor × nº alunos. Cada idioma cobra na sua moeda.</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {[
              { key: "pt", flag: "🇵🇹", label: "Português (Europa)" },
              { key: "br", flag: "🇧🇷", label: "Português (Brasil)" },
              { key: "en", flag: "🇬🇧", label: "English" },
            ].map(({ key, flag, label }) => (
              <div key={key} className="rounded-xl border border-[#0B1D3A]/10 p-3 space-y-2" data-testid={`price-block-${key}`}>
                <p className="text-sm font-semibold">{flag} {label}</p>
                <label className="block text-xs text-[#0B1D3A]/60">Valor por aluno
                  <input type="number" step="0.01" min="0" value={cfg[`enrollment_price_${key}`]}
                    onChange={(e) => setC(`enrollment_price_${key}`, parseFloat(e.target.value) || 0)}
                    className="w-full mt-1 px-3 py-2 rounded-lg border border-[#0B1D3A]/15 text-sm" data-testid={`enrollment-price-${key}`} />
                </label>
                <label className="block text-xs text-[#0B1D3A]/60">Moeda
                  <select value={cfg[`enrollment_currency_${key}`]}
                    onChange={(e) => setC(`enrollment_currency_${key}`, e.target.value)}
                    className="w-full mt-1 px-3 py-2 rounded-lg border border-[#0B1D3A]/15 text-sm bg-white" data-testid={`enrollment-currency-${key}`}>
                    <option value="eur">EUR (€)</option>
                    <option value="brl">BRL (R$)</option>
                    <option value="gbp">GBP (£)</option>
                    <option value="usd">USD ($)</option>
                  </select>
                </label>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-[#0B1D3A]/50">MB WAY e Multibanco só funcionam em EUR. BRL tem Cartão + PayPal + PIX. Cartão e PayPal aceitam qualquer moeda.</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {["pt","br","en"].map((k) => (
              <label key={k} className="block text-sm">Instruções ({k.toUpperCase()})
                <textarea rows="4" value={cfg[`payment_instructions_${k}`]} onChange={(e) => setC(`payment_instructions_${k}`, e.target.value)} className="w-full mt-1 px-3 py-2 rounded-lg border border-[#0B1D3A]/15" data-testid={`payment-instructions-${k}`}/>
              </label>
            ))}
          </div>
          <label className="block text-sm max-w-xs">Nº de alunos mostrados nos resultados (0 = todos)
            <input type="number" min="0" value={cfg.results_show_count} onChange={(e) => setC("results_show_count", parseInt(e.target.value) || 0)} className="w-full mt-1 px-3 py-2 rounded-lg border border-[#0B1D3A]/15" data-testid="results-show-count"/>
          </label>
          <button onClick={saveCfg} disabled={savingCfg} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#0B1D3A] text-white font-bold disabled:opacity-60" data-testid="payments-config-save">
            {savingCfg ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Guardar
          </button>
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0B1D3A]/40" />
          <input type="text" value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="Pesquisar código, professor, email, escola, turma, concurso, método, doc..."
            className="w-full pl-10 pr-10 py-2.5 rounded-lg border border-[#0B1D3A]/15 focus:border-[#005F73] outline-none text-sm"
            data-testid="enroll-search-input" />
          {query && (<button onClick={() => setQuery("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#0B1D3A]/40 hover:text-[#0B1D3A] text-lg leading-none" data-testid="enroll-search-clear">×</button>)}
        </div>
        <div className="flex gap-2 flex-wrap">
          {["all", "paid", "pending"].map((f) => (
            <button key={f} onClick={() => setFilter(f)} data-testid={`enroll-filter-${f}`}
              className={`px-3 py-1.5 rounded-full text-sm font-semibold transition ${filter === f ? "bg-[#0B1D3A] text-white" : "bg-[#0B1D3A]/5 text-[#0B1D3A]"}`}>
              {{ all: "Todos", paid: "Pagos", pending: "Pendentes" }[f]}
            </button>
          ))}
          <button onClick={removeSelected} disabled={selected.size === 0} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-500 text-white font-semibold text-sm disabled:opacity-40" data-testid="enroll-delete-selected-btn"><Trash2 size={15} /> Eliminar{selected.size > 0 ? ` (${selected.size})` : ""}</button>
          <button onClick={removeAll} disabled={items.length === 0} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-600 text-white font-semibold text-sm disabled:opacity-40" data-testid="enroll-delete-all-btn"><Trash2 size={15} /> Todas</button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4 text-sm">
        <span className="text-[#0B1D3A]/60 font-semibold">Alunos:</span>
        <input type="number" min="0" inputMode="numeric" value={studentsMin} onChange={(e) => setStudentsMin(e.target.value)}
          placeholder="min" className="w-20 px-2 py-1.5 rounded-md border border-[#0B1D3A]/15 focus:border-[#005F73] outline-none"
          data-testid="enroll-students-min" />
        <span className="text-[#0B1D3A]/40">–</span>
        <input type="number" min="0" inputMode="numeric" value={studentsMax} onChange={(e) => setStudentsMax(e.target.value)}
          placeholder="max" className="w-20 px-2 py-1.5 rounded-md border border-[#0B1D3A]/15 focus:border-[#005F73] outline-none"
          data-testid="enroll-students-max" />
        {(studentsMin !== "" || studentsMax !== "" || studentsSort) && (
          <button onClick={() => { setStudentsMin(""); setStudentsMax(""); setStudentsSort(null); }}
            className="text-xs text-[#0B1D3A]/60 hover:text-[#0B1D3A] underline" data-testid="enroll-students-clear">
            limpar
          </button>
        )}
      </div>
      {query && <p className="text-xs text-[#0B1D3A]/50 mb-3" data-testid="enroll-search-count">{filtered.length} de {items.length} inscrição(ões)</p>}

      <div className="rounded-2xl bg-white border border-[#0B1D3A]/5 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F8FAFC] text-[#0B1D3A]/70">
            <tr>
              <th className="px-3 py-3 w-10"><input type="checkbox" checked={allSelected} onChange={toggleAll} data-testid="enroll-select-all" className="cursor-pointer" /></th>
              <th className="text-left px-3 py-3">Código</th>
              <th className="text-left px-3 py-3">Data</th>
              <th className="text-left px-3 py-3">Professor</th>
              <th className="text-left px-3 py-3">Escola / Turma</th>
              <th className="text-right px-3 py-3">
                <button onClick={cycleStudentsSort} className="inline-flex items-center gap-1 font-semibold hover:text-[#0B1D3A]" data-testid="enroll-sort-students" title="Clicar para ordenar">
                  Alunos
                  <span className="text-[10px] opacity-70">{studentsSort === "asc" ? "▲" : studentsSort === "desc" ? "▼" : "↕"}</span>
                </button>
              </th>
              <th className="text-left px-3 py-3">Concurso</th>
              <th className="text-left px-3 py-3">Método</th>
              <th className="text-right px-3 py-3">Montante</th>
              <th className="text-left px-3 py-3">Doc.</th>
              <th className="text-left px-3 py-3">Estado</th>
              <th className="text-left px-3 py-3">Credenciais</th>
              <th className="text-right px-3 py-3">Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan="13" className="px-4 py-8 text-center text-[#0B1D3A]/50"><Loader2 className="animate-spin inline" size={20} /></td></tr>}
            {!loading && filtered.map((e) => {
              const lp = e.latest_payment;
              const isPaid = e.payment_confirmed;
              return (
                <tr key={e.id} className="border-t border-[#0B1D3A]/5 align-top" data-testid={`enroll-row-${e.id}`}>
                  <td className="px-3 py-3"><input type="checkbox" checked={selected.has(e.id)} onChange={() => toggleOne(e.id)} data-testid={`enroll-select-${e.id}`} className="cursor-pointer" /></td>
                  <td className="px-3 py-3 font-mono text-[#005F73] font-semibold whitespace-nowrap" data-testid={`enroll-code-${e.id}`}>{e.code || "—"}</td>
                  <td className="px-3 py-3 text-[#0B1D3A]/60 whitespace-nowrap text-xs">{fmtDateShort(e.created_at)}</td>
                  <td className="px-3 py-3"><div className="font-medium">{e.teacher_name}</div><div className="text-xs text-[#0B1D3A]/50">{e.teacher_email}</div></td>
                  <td className="px-3 py-3"><div>{e.school}</div><div className="text-xs text-[#0B1D3A]/50">{e.class_name}</div></td>
                  <td className="px-3 py-3 text-right font-bold">{e.num_students}</td>
                  <td className="px-3 py-3">{e.quiz_name_pt}</td>
                  <td className="px-3 py-3 whitespace-nowrap">{lp ? (PROVIDER_LABEL[lp.provider] || lp.provider) : <span className="text-[#0B1D3A]/40">—</span>}</td>
                  <td className="px-3 py-3 text-right font-bold text-[#0B1D3A] whitespace-nowrap">{lp ? money(lp.amount, lp.currency) : <span className="text-[#0B1D3A]/40">—</span>}</td>
                  <td className="px-3 py-3">{lp
                    ? <DocRefCell initial={lp.doc_ref || ""} url={`/admin/payments/${lp.id}/doc-ref`} testId={`payment-doc-ref-${lp.id}`} onSaved={(v) => updateDocRef(lp.id, v)} />
                    : <DocRefCell initial={e.doc_ref || ""} url={`/admin/enrollments/${e.id}/doc-ref`} testId={`enrollment-doc-ref-${e.id}`} onSaved={(v) => updateEnrollmentDocRef(e.id, v)} />
                  }</td>
                  <td className="px-3 py-3">
                    <div className="flex flex-col gap-1">
                      {isPaid
                        ? <span className="text-xs px-2 py-1 rounded-full bg-[#2D9C5A]/15 text-[#2D9C5A] font-semibold w-fit">Pago</span>
                        : <span className="text-xs px-2 py-1 rounded-full bg-[#FFC107]/20 text-[#856404] font-semibold w-fit">Pendente</span>}
                      {(() => {
                        const ts = isPaid ? e.payment_confirmed_at : (lp?.created_at || e.created_at);
                        return ts ? <span className="text-[10px] text-[#0B1D3A]/50 whitespace-nowrap">{fmtDateShort(ts)}</span> : null;
                      })()}
                      {lp && (lp.provider === "multibanco" || lp.provider === "pix") && (lp.reference || lp.entity) && (
                        <span className="text-[10px] font-mono text-[#0B1D3A]/70 leading-tight">
                          {lp.entity ? <>Ent. <b>{lp.entity}</b> · </> : null}Ref. <b>{lp.reference || "—"}</b>
                        </span>
                      )}
                      {lp && lp.provider === "mbway" && (lp.terminal || lp.transaction_id) && (
                        <span className="text-[10px] font-mono text-[#0B1D3A]/70 leading-tight break-all">
                          {lp.terminal ? <>Term. <b>{lp.terminal}</b></> : null}
                          {lp.terminal && lp.transaction_id ? " · " : null}
                          {lp.transaction_id ? <>ID <b>{lp.transaction_id}</b></> : null}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-3">
                    {e.credentials_sent
                      ? <span className="text-xs px-2 py-1 rounded-full bg-[#005F73]/15 text-[#005F73] font-semibold">Enviadas</span>
                      : <span className="text-xs text-[#0B1D3A]/40">—</span>}
                  </td>
                  <td className="px-3 py-3 text-right">
                    <div className="inline-flex gap-1.5 items-center flex-wrap justify-end">
                      {!isPaid && (
                        <button onClick={() => confirmPayment(e)} className="inline-flex items-center gap-1 text-xs px-2 py-1.5 rounded-md bg-[#2D9C5A]/10 text-[#2D9C5A] hover:bg-[#2D9C5A]/20 font-semibold" data-testid={`enroll-confirm-${e.id}`} title="Marcar como pago"><CheckCircle2 size={14} /> Pago</button>
                      )}
                      {isPaid && lp && (
                        <button onClick={() => revertPayment(e)} className="inline-flex items-center gap-1 text-xs px-2 py-1.5 rounded-md bg-amber-100 text-amber-700 hover:bg-amber-200 font-semibold" data-testid={`enroll-revert-${e.id}`} title="Reverter para pendente"><RotateCcw size={14} /> Reverter</button>
                      )}
                      <button onClick={() => sendCreds(e)} disabled={!isPaid} title={isPaid ? "Enviar credenciais" : "Confirme o pagamento primeiro"} className="inline-flex items-center gap-1 text-xs px-2 py-1.5 rounded-md bg-[#005F73]/10 text-[#005F73] hover:bg-[#005F73]/20 font-semibold disabled:opacity-40 disabled:cursor-not-allowed" data-testid={`enroll-send-${e.id}`}><Mail size={14} /> Credenciais</button>
                      <button onClick={() => removeEnrollment(e.id)} className="text-red-600" data-testid={`enroll-delete-${e.id}`}><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {!loading && filtered.length === 0 && <tr><td colSpan="13" className="px-4 py-8 text-center text-[#0B1D3A]/50">{items.length === 0 ? "Sem inscrições ainda." : "Nenhuma inscrição corresponde ao filtro."}</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DocRefCell({ initial, url, testId, onSaved }) {
  const [value, setValue] = useState(initial || "");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setValue(initial || ""); }, [initial]);
  const save = async () => {
    const v = value.trim();
    if (v === (initial || "")) return;
    setSaving(true);
    try {
      await api.put(url, { doc_ref: v });
      onSaved(v);
      toast.success("Doc. guardado");
    } catch (_) { toast.error("Erro"); setValue(initial || ""); }
    finally { setSaving(false); }
  };
  return (
    <input type="text" value={value} onChange={(e) => setValue(e.target.value)}
      onBlur={save} onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
      disabled={saving} placeholder="—" title="Doc. contabilístico"
      data-testid={testId}
      className="w-24 px-2 py-1.5 rounded-md border border-[#0B1D3A]/15 focus:border-[#005F73] outline-none text-xs disabled:opacity-50" />
  );
}
